import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";

/* ════════════════════════════════════════════════════════════════════════════ */
const CheckInCheckOutCard = ({
  today,
  error,
  isLoading,
  setShowLeaveModal,
  employeeId,
  employeeName,
  SERVER_URL,
  isOnLeaveToday,
  leaveTypeId,
  leaveStatus,
  onAttendanceUpdated,
}) => {
  const [checkInStatus, setCheckInStatus] = useState(false);
  const [checkOutStatus, setCheckOutStatus] = useState(false);
  const [timeStamp, setTimeStamp] = useState({
    checkInTime: "",
    checkOutTime: "",
  });

  const isLeaveBlockingAttendance =
    isOnLeaveToday &&
    leaveStatus === "approved" &&
    leaveTypeId !== 5; // block everything except WFH

  useEffect(() => {
    fetchCheckInStatus(employeeId, employeeName);
  }, [employeeId, employeeName]);

  const formatTime = (timeString) => {
    if (!timeString) return "—";
    const [hours, minutes] = timeString.split(":");
    return `${hours}:${minutes}`;
  };

  const getCurrentTime = () => {
    const now = new Date();
    return now.toTimeString().slice(0, 8);
  };

  const submitAttendance = async (type) => {
  try {

    //------------------------------------------------
    // GET GPS LOCATION (MANDATORY)
    //------------------------------------------------
    let latitude = null;
    let longitude = null;
    let location_name = null;

    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    }).catch((err) => {
      console.error("Location Error:", err);
      return null;
    });

    //------------------------------------------------
    // BLOCK WITHOUT LOCATION
    //------------------------------------------------
    if (!position) {
      alert("Location access is required for attendance.");
      return;
    }

    latitude = position.coords.latitude;
    longitude = position.coords.longitude;

    //------------------------------------------------
    // GET LOCATION NAME
    //------------------------------------------------
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
      );

      const data = await res.json();
      location_name = data.display_name || null;

    } catch (locErr) {
      console.warn("Location name fetch failed:", locErr);
    }

    //------------------------------------------------
    // API CALL
    //------------------------------------------------
    const payload = {
      employee_id: employeeId,
      type,
      latitude,
      longitude,
      location_name,
    };

    const response = await axios.post(
      `${SERVER_URL}/DataInsert/submitAttendance.php`,
      payload
    );

    //------------------------------------------------
    // SUCCESS
    //------------------------------------------------
    if (response.data.status === true) {

      if (type === "checkIn") {
        setCheckInStatus(true);
        setTimeStamp((prev) => ({
          ...prev,
          checkInTime: response.data.saved_time,
        }));

        alert("Check-In done successfully.");
      }

      if (type === "checkOut") {
        setCheckOutStatus(true);
        setTimeStamp((prev) => ({
          ...prev,
          checkOutTime: response.data.saved_time,
        }));

        alert("Check-Out done successfully.");
      }

      window.dispatchEvent(new Event("attendance-updated"));

      fetchCheckInStatus(employeeId, employeeName);

      if (onAttendanceUpdated) {
        onAttendanceUpdated();
      }

    } else {
      //------------------------------------------------
      // API ERROR
      //------------------------------------------------
      alert(response.data.message || "Attendance failed");
    }

  } catch (error) {
    console.error("Attendance error:", error);

    alert("Attendance submission failed.");
  }
};

  const fetchCheckInStatus = async (employeeId, employeeName) => {
    try {
      const payload = { employee_id: employeeId || null, employee_name: employeeName || null };
      const res = await axios.post(`${SERVER_URL}/DataFetch/checkInStatus.php`, payload);
      if (res.data.status == true) {
        setCheckOutStatus(res.data.checkout);
        setCheckInStatus(res.data.checkin);
        setTimeStamp({
          checkInTime: res.data.checkInTime || "",
          checkOutTime: res.data.checkOutTime || "",
        });
      }
    } catch (error) {
      console.error("Error fetching check-in status:", error);
    }
  };

  console.log("isOnLeaveToday:::::", isOnLeaveToday);

  /* ════════════════════════════════ JSX ════════════════════════════════ */
  return (
    <>
      <style>{`
        .att-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .att-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      {/* White card — matches AdminAttendanceOverview filter bar */}
      <div
        className="rounded-xl px-5 py-4 bg-white border border-slate-200"
        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}
      >
        {/* Card header */}
        <div className="flex items-center gap-2 mb-4">
          <div
            className="w-1 h-5 rounded-full"
            style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }}
          />
          <CalendarDaysIcon className="w-4 h-4 text-blue-700" />
          <h2 className="text-sm font-bold text-slate-800 m-0 tracking-tight">
            Today's Attendance
          </h2>
          <span className="ml-1 text-xs font-medium text-slate-400">({today})</span>
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">

          {/* Time display */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-1">

            <div className="flex flex-col gap-0.5 px-4 py-3 rounded-lg border border-slate-200 bg-slate-50 min-w-[120px]">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Check In</span>
              <span className="text-base font-bold text-slate-800 font-mono tracking-tight">
                {formatTime(timeStamp.checkInTime)}
              </span>
            </div>

            <div className="flex flex-col gap-0.5 px-4 py-3 rounded-lg border border-slate-200 bg-slate-50 min-w-[120px]">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Check Out</span>
              <span className="text-base font-bold text-slate-800 font-mono tracking-tight">
                {formatTime(timeStamp.checkOutTime)}
              </span>
            </div>

          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">

            {/* Apply Leave */}
            <button
              onClick={() => setShowLeaveModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors flex-1 min-w-[120px]"
            >
              <PlusCircleIcon className="w-4 h-4" />
              Apply Leave
            </button>

            {/* Check In */}
            <button
              onClick={() => submitAttendance("checkIn")}
              disabled={isLeaveBlockingAttendance}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors flex-1 min-w-[120px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircleIcon className="w-4 h-4" />
              Check In
            </button>

            {/* Check Out */}
            <button
              onClick={() => submitAttendance("checkOut")}
              disabled={isLeaveBlockingAttendance}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-rose-700 bg-rose-50 border border-rose-200 hover:bg-rose-100 transition-colors flex-1 min-w-[120px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircleIcon className="w-4 h-4" />
              Check Out
            </button>

          </div>
        </div>

        {error && (
          <p className="text-rose-600 text-sm mt-3 font-medium">{error}</p>
        )}
      </div>
    </>
  );
};

export default CheckInCheckOutCard;